import { getCurrentAddress as getAddressValidatorCurrentAddress } from '@product-framework/components/FormFields/AddressValidator';
import { getCurrentAddress as getValidatedAddressCurrentAddress } from '@product-framework/components/FormFields/ValidatedAddress';
import createServiceRequestRowSchema from '@product-framework/validationHelpers/createServiceRequestRowSchema';
import { Engine } from 'json-rules-engine';

class SJFormHelper {
  constructor({ navigate, dataProvider, yup, formatMessage, masterData }) {
    this.redirect = navigate;
    this.dataProvider = dataProvider;
    this.yup = yup;
    this.formatMessage = formatMessage;
    this.masterData = masterData;
  }

  static getAdditonalParamsToInject({ requiredParams, availableParams }) {
    let returnParams = {};
    Object.entries(availableParams).forEach(([key, value]) => {
      if (requiredParams.includes(key)) {
        returnParams = {
          ...returnParams,
          [key]: value,
        };
      }
    });
    return returnParams;
  }

  static interpolateUrl(urlTemplate, params) {
    return urlTemplate.replace(/{([^}]+)}/g, (_, key) => params[key] ?? '');
  }

  basicValidation({ validations, validator }) {
    let currentValidator = validator;
    validations.forEach(rule => {
      const message = this.formatMessage({
        id: rule.locale.id,
        defaultMessage: rule.locale.defaultMessage,
      });
      switch (rule.type) {
        case 'required':
          currentValidator = currentValidator.required(message);
          break;
        case 'max':
          currentValidator = currentValidator.max(rule.max, message);
          break;
        case 'min':
          currentValidator = currentValidator.min(rule.min, message);
          break;
        default:
          break;
      }
    });
    return currentValidator;
  }

  shapeValidation({ fields, validator }) {
    let validationObjects = {};
    fields.forEach(fieldItem => {
      validationObjects = {
        ...validationObjects,
        [fieldItem.name]: this.getValidations({
          name: fieldItem.name,
          validations: fieldItem?.validations || [],
        }),
      };
    });

    return validator.of(this.yup.object().shape(validationObjects));
  }

  getValidations({ validations }) {
    let validator = this.yup.string().nullable();
    validations.some(rule => {
      if (rule.type === 'custom') {
        if (rule.name === 'createServiceRequestRowSchema') {
          validator = this.yup.array();
          validator = validator
            .of(createServiceRequestRowSchema(rule?.locale?.defaultMessage))
            .nullable()
            .test('inject-row-count', '', function injectRowCount(rows) {
              this.options.context = {
                ...this.options.context,
                rowCount: rows?.length ?? 0,
              };
              return true;
            });
          return true;
        }
        return false;
      }
      if (rule.type === 'array') {
        validator = this.yup.array();
        if (rule?.fields) {
          validator = this.shapeValidation({ fields: rule?.fields, validator });
        } else {
          validator = validator.nullable();
        }
        return true;
      }
      if (rule.type === 'object') {
        validator = this.yup.object().nullable();
        return true;
      }
      return false;
    });
    validator = this.basicValidation({ validations, validator });

    return validator;
  }

  static isValidFieldForFormData(field) {
    return !field.readOnly && !(field?.register === false);
  }

  getValidationObjects({ formSchema }) {
    let validationObjects = {};
    formSchema.PAGESECTIONS.forEach(formItem => {
      formItem.children.forEach(field => {
        if (field.children && field.children.length > 0) {
          field.children.forEach(childField => {
            if (SJFormHelper.isValidFieldForFormData(childField)) {
              if (childField?.validations) {
                validationObjects = {
                  ...validationObjects,
                  [childField.name]: this.getValidations({
                    name: childField.name,
                    validations: childField?.validations || [],
                  }),
                };
              }
            }
          });
        } else if (SJFormHelper.isValidFieldForFormData(field)) {
          if (field.validations) {
            validationObjects = {
              ...validationObjects,
              [field.name]: this.getValidations({
                name: field.name,
                validations: field?.validations || [],
              }),
            };
          }
        }
      });
    });

    return this.yup.object(validationObjects).required();
  }

  getApiMethodCaller({ method }) {
    switch (method) {
      case 'POST':
        return this.dataProvider.post;
      case 'GET':
        return this.dataProvider.get;
      case 'PUT':
        return this.dataProvider.put;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  }

  call({ method, url, params, data = {} }) {
    const apiMethod = this.getApiMethodCaller({
      method,
    });
    const urlToCall = `${this.dataProvider.getApiUrl()}${SJFormHelper.interpolateUrl(
      url,
      params,
    )}`;
    return apiMethod(urlToCall, data);
  }

  navigate({ url, params }) {
    this.redirect(SJFormHelper.interpolateUrl(url, params));
  }

  static async runRuleEngine({ rules, params = {} }) {
    const engine = new Engine();
    // eslint-disable-next-line no-restricted-syntax
    for (const rule of rules) {
      // eslint-disable-next-line no-await-in-loop
      await engine.addRule(rule);
    }
    try {
      const result = await engine.run(params);
      return result;
    } catch (e) {
      return e.message;
    }
  }

  static removeAttributes({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = { ...obj };
    attributes.forEach(attr => {
      delete newObj[attr];
    });
    return newObj;
  }

  static onlyAttributes({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = {};
    attributes.forEach(attr => {
      if (Object.prototype.hasOwnProperty.call(obj, attr)) {
        newObj[attr] = obj[attr];
      }
    });
    return newObj;
  }

  /*
    {
      "type": "OBJECT_TO_VALUE",
      "params": [
        {
            "customerPlaceId": "id"
        }
      ]
    }
  */
  static objectToValue({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = obj;
    attributes.forEach(attr => {
      const attrKeyValue = Object.entries(attr)?.[0] || [];
      const attrNameKey = attrKeyValue?.[0];
      const attrValueKey = attrKeyValue?.[1];
      if (Object.prototype.hasOwnProperty.call(obj, attrNameKey)) {
        newObj[attrNameKey] = obj[attrNameKey]?.[attrValueKey] ?? '';
      }
    });
    return newObj;
  }

  // attributes is an array of object {old, new}
  static renameAttributes({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = {};
    attributes.forEach(attr => {
      if (Object.prototype.hasOwnProperty.call(obj, attr.old)) {
        newObj[attr.new] = obj[attr.old];
      }
    });
    return newObj;
  }

  static convertFieldValueToBoolean({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = { ...obj };
    attributes.forEach(attr => {
      const fieldExists = Object.keys(obj).some(key => key === attr);
      if (fieldExists) {
        newObj[attr] = ['true', true, 'Yes', 'Active'].includes(newObj[attr]);
      }
    });
    return newObj;
  }

  static convertEmptyFieldValueToNull({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = { ...obj };
    attributes.forEach(attr => {
      if (Object.prototype.hasOwnProperty.call(newObj, attr)) {
        if (
          newObj[attr] === '' ||
          newObj[attr] === undefined ||
          newObj[attr] === null
        ) {
          newObj[attr] = null;
        }
      }
    });
    return newObj;
  }

  // create a function that will
  //   give and entity object { id: { label, value }, att1: '', att2: '' }
  // return only { id: value }
  //   do this only if the name field as dot notation in it

  static convertFieldLabelValueToValue({ obj, attributes }) {
    if (!obj || typeof obj !== 'object') return obj;
    const newObj = { ...obj };
    attributes.forEach(attr => {
      const fieldExists = Object.keys(obj).some(key => key === attr);
      if (fieldExists) {
        if (newObj[attr]?.value) {
          newObj[attr] = {
            id: newObj[attr]?.value,
          };
        } else {
          newObj[attr] = {
            id: null,
          };
        }
      }
    });
    return newObj;
  }

  static getValueByControl({
    control,
    value,
    CustomComponentConfig,
    data,
    controlSchema,
    permissions,
  }) {
    if (control === 'YesNo') {
      return value || false;
    }
    if (control === 'AddressValidator') {
      const newValue = getAddressValidatorCurrentAddress(
        data,
        controlSchema?.dependencies,
        controlSchema?.dependencyPermissions,
        permissions,
      );
      return newValue;
    }

    if (control === 'ValidatedAddress') {
      const newValue = getValidatedAddressCurrentAddress({
        zip: data?.customerAddressPostalCode,
        city: data?.customerAddressCity,
        state: data?.customerAddressState?.name,
        address: data?.customerAddressStreet,
        country: data?.customerAddressCountry?.countryName,
        id: data?.id || null,
      });
      return newValue;
    }

    if (CustomComponentConfig[control]) {
      const customConfig = CustomComponentConfig[control];
      const customControl = customConfig?.control;
      switch (customControl) {
        case 'autocompletev2':
        case 'StatesSelector':
        case 'autocomplete': {
          // Due to button handling, initial values are sometimes received as {label,value} instead of entity object, when api calls fail
          if (
            value &&
            Object.prototype.hasOwnProperty.call(value, 'value') &&
            Object.prototype.hasOwnProperty.call(value, 'label')
          ) {
            return value;
          }
          if (value?.[customConfig?.valueKey]) {
            return {
              label: value?.[customConfig?.nameKey] || '',
              value: value?.[customConfig?.valueKey] || '',
            };
          }
          return null;
        }
        default:
          return value || '';
      }
    }
    if (control === 'Parts') {
      return value || [];
    }
    const otherAutoComplete = {
      Countries: {
        nameKey: 'countryName',
      },
      PurchasedFromLocation: {
        nameKey: 'locationName',
      },
      Brands: {
        nameKey: 'brandName',
      },
      ServiceProviderLocationNumber: {
        nameKey: 'locationNumber',
      },
    };

    if (otherAutoComplete?.[control]) {
      if (value?.id) {
        return {
          label: value[otherAutoComplete?.[control]?.nameKey] || '',
          value: value?.id || '',
        };
      }
      return null;
    }

    return value || '';
  }

  getFormInitialValues({
    formSchema,
    data = {},
    customComponents = {},
    permissions = {},
  }) {
    if (!formSchema || !formSchema.PAGESECTIONS) {
      return {};
    }
    const initialValues = data;
    formSchema.PAGESECTIONS.forEach(section => {
      section.children.forEach(field => {
        if (
          (field.children && field.children.length > 0) ||
          field.control === 'subsection'
        ) {
          field.children.forEach(childField => {
            const createValue = SJFormHelper.getValueByControl({
              control: childField.control,
              value: data?.[childField?.name],
              CustomComponentConfig: customComponents.CustomComponentConfigs(
                this.masterData,
              ),
              data: initialValues,
              controlSchema: childField,
              permissions,
            });
            initialValues[childField.name] = createValue;
          });
        } else {
          const finalValue = SJFormHelper.getValueByControl({
            control: field.control,
            value: data[field.name],
            CustomComponentConfig: customComponents.CustomComponentConfigs(
              this.masterData,
            ),
            data: initialValues,
            formSchema,
            controlSchema: field,
            permissions,
          });
          initialValues[field.name] = finalValue;
        }
      });
    });
    return initialValues;
  }

  static async getPageSections(pageSectionsArg, formValues) {
    const results = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const pageSection of pageSectionsArg || []) {
      if (pageSection?.children.length > 0) {
        if (pageSection?.visibility?.rules) {
          // eslint-disable-next-line no-await-in-loop
          const result = await SJFormHelper.runRuleEngine({
            rules: pageSection.visibility?.rules,
            params: {
              ...formValues,
            },
          });
          if (Array.isArray(result) && result.length > 0) {
            if (result[0].type === 'RETURN_TRUE') {
              results.push(pageSection);
            }
          }
        } else {
          results.push(pageSection);
        }
      }
    }
    return results;
  }

  static async getPageButtons(buttons, formValues) {
    const results = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const button of buttons || []) {
      if (button?.visibility?.rules) {
        // eslint-disable-next-line no-await-in-loop
        const result = await SJFormHelper.runRuleEngine({
          rules: button.visibility?.rules,
          params: {
            ...formValues,
          },
        });
        if (Array.isArray(result) && result.length > 0) {
          if (result[0].type === 'RETURN_TRUE') {
            results.push(button);
          }
        }
      } else {
        results.push(button);
      }
    }
    return results;
  }
}

export default SJFormHelper;
